import React, { useState } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Alert,
  Link,
  CircularProgress,
  Divider,
  Stack,
} from '@mui/material';
import { Google as GoogleIcon, GitHub as GitHubIcon } from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';

interface LoginFormProps {
  onSwitchToSignup: () => void;
  onSwitchToReset: () => void;
}

export const LoginForm: React.FC<LoginFormProps> = ({
  onSwitchToSignup,
  onSwitchToReset,
}) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [oauthLoading, setOauthLoading] = useState<string | null>(null);
  const { signIn, signInWithGoogle, signInWithGitHub } = useAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const { error } = await signIn(email, password);
      if (error) {
        setError(error.message);
      }
    } catch (err) {
      setError('An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleOAuthSignIn = async (provider: 'google' | 'github') => {
    setError(null);
    setOauthLoading(provider);

    try {
      const { error } = provider === 'google' 
        ? await signInWithGoogle() 
        : await signInWithGitHub();
      
      if (error) {
        setError(error.message);
      }
    } catch (err) {
      setError('An unexpected error occurred');
    } finally {
      setOauthLoading(null);
    }
  };

  return (
    <Paper elevation={3} sx={{ p: 4, maxWidth: 400, mx: 'auto', mt: 8 }}>
      <Typography variant="h4" component="h1" gutterBottom align="center">
        Sign In
      </Typography>
      <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 3 }}>
        Access your Nevada Gaming Compliance AI Assistant
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box component="form" onSubmit={handleSubmit}>
        <TextField
          fullWidth
          label="Email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          margin="normal"
          required
          autoComplete="email"
          autoFocus
        />
        <TextField
          fullWidth
          label="Password"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          margin="normal"
          required
          autoComplete="current-password"
        />
        <Button
          type="submit"
          fullWidth
          variant="contained"
          sx={{ mt: 3, mb: 2 }}
          disabled={loading || oauthLoading !== null}
        >
          {loading ? <CircularProgress size={24} /> : 'Sign In'}
        </Button>
      </Box>

      <Divider sx={{ my: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Or continue with
        </Typography>
      </Divider>

      <Stack spacing={2} sx={{ mb: 2 }}>
        <Button
          fullWidth
          variant="outlined"
          startIcon={oauthLoading === 'google' ? <CircularProgress size={20} /> : <GoogleIcon />}
          onClick={() => handleOAuthSignIn('google')}
          disabled={loading || oauthLoading !== null}
        >
          {oauthLoading === 'google' ? 'Connecting...' : 'Continue with Google'}
        </Button>
        <Button
          fullWidth
          variant="outlined"
          startIcon={oauthLoading === 'github' ? <CircularProgress size={20} /> : <GitHubIcon />}
          onClick={() => handleOAuthSignIn('github')}
          disabled={loading || oauthLoading !== null}
        >
          {oauthLoading === 'github' ? 'Connecting...' : 'Continue with GitHub'}
        </Button>
      </Stack>

      <Box sx={{ textAlign: 'center' }}>
        <Link
          component="button"
          variant="body2"
          onClick={onSwitchToReset}
          sx={{ display: 'block', mb: 1 }}
        >
          Forgot your password?
        </Link>
        <Divider sx={{ my: 2 }} />
        <Typography variant="body2">
          Don't have an account?{' '}
          <Link component="button" onClick={onSwitchToSignup}>
            Sign up
          </Link>
        </Typography>
      </Box>
    </Paper>
  );
};